import { useRef, Suspense } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Hero3D from "./Hero3D";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const canvasFade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative h-[100svh] w-full overflow-hidden bg-[#0f1724]">
      {/* Ambient glow + technical grid */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] bg-[#b88457] opacity-[0.07] blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute inset-0 tech-grid opacity-40" />

      {/* 3D wireframe pressure vessel */}
      <motion.div style={{ opacity: canvasFade }} className="absolute inset-0 z-[1]">
        <Suspense fallback={null}>
          <Hero3D />
        </Suspense>
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-t from-[#0f1724] via-transparent to-[#0f1724]/40 pointer-events-none z-[2]" />

      {/* Scanning line */}
      <motion.div
        className="absolute left-0 right-0 h-24 scanline pointer-events-none z-[3]"
        initial={{ top: "-10%" }}
        animate={{ top: "110%" }}
        transition={{ duration: 5, ease: "linear", repeat: Infinity, repeatDelay: 2 }}
      />

      {/* Content */}
      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="font-mono-data text-[#b88457] text-[11px] tracking-[0.35em] mb-8 flex items-center gap-4"
        >
          <span className="w-2 h-2 bg-[#b88457]" />
          THE INVESTIGATION INTELLIGENCE PLATFORM
        </motion.div>

        <h1 className="font-display font-medium text-white leading-[0.98] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl">
          <Line delay={0.35}>
            Truth leaves a trail<span className="text-[#b88457]">.</span>
          </Line>
          <Line delay={0.5}>We help investigators</Line>
          <Line delay={0.62}>follow it.</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed font-light">
            Every incident begins with an origin. Every failure has a root. Asritas helps
            investigators reconstruct what happened, uncover why it happened, and build conclusions
            that can withstand technical, operational, and regulatory scrutiny. The reasoning is
            strengthened. The judgment remains human.
          </p>
          <div className="font-mono-data text-[10px] tracking-[0.2em] text-zinc-500 space-y-1 md:text-right shrink-0">
            <div>CASE · REACTOR UNIT 4</div>
            <div>LOPC-2 · SHELL RUPTURE</div>
            <div className="text-[#b88457]">STATUS: RESOLVED</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono-data text-[10px] tracking-[0.3em] text-zinc-500 flex flex-col items-center gap-2"
      >
        SCROLL TO INVESTIGATE
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-6 bg-gradient-to-b from-[#b88457] to-transparent"
        />
      </motion.div>
    </section>
  );
}
